import { Article, Head, StructuredData } from '@/components';
import LandingPageSEOHead from '@/components/LandingPageSEOHead';
import { LandingPageRenderer, PermutationPageRenderer, SEOPageRenderer } from '@/components/SectionListRenderer';
import {
  AmpPageType,
  CONNECTING_TO_CONTENTFUL,
  CONNECTING_TO_REDIS,
  ContentTypeName,
  SUPPORTED_LOCALES,
  ThunkDebugName,
  serverSharedData,
} from '@/constants';
import { getAllThumbnails, getArticle, getLandingPage, getTag } from '@/fetchers';
import { utils_getRelatedArticles } from '@/fetchers/utils';
import type { IArticleProps, ILandingPage, ITag, IThumbnailSection, ThumbnailProps } from '@/interfaces';
import { capitalize, checkIsPreviewModeRequest, getArticleTypeNameFromSlug } from '@/utils';
import { getArticleStructuredData, removeOneArticleSensitiveData } from '@/utils/articleDataModify.utils';
import createServerSidePropsResult, { type IThunk } from '@/utils/createServerSidePropsResult.utils';
import {
  ORGANIZATION_SCHEMA,
  SOFTWARE_APP_SCHEMA,
  extractFaqItemsFromRichText,
  getFaqSchema,
} from '@/utils/seoPageSchemas.utils';
import type { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import getConfig from 'next/config';

const { serverRuntimeConfig } = getConfig();

const ArticlePage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  articlePermutationContent,
  articleContent,
  relatedArticles,
  landingPageData,
  seoPageData,
  webVersion,
}) => {
  // biome-ignore lint/suspicious/noConsoleLog: <explanation>
  console.log(webVersion);

  if (articlePermutationContent) {
    return (
      <>
        {articlePermutationContent.seo && <Head {...articlePermutationContent.seo} />}
        <PermutationPageRenderer
          sectionList={articlePermutationContent.sectionList || []}
          title={articlePermutationContent.title}
          breadcrumbsTheme={articlePermutationContent.breadcrumbsTheme}
        />
      </>
    );
  }

  if (articleContent) {
    return (
      <>
        {articleContent.seo && <Head {...articleContent.seo} />}
        <StructuredData data={getArticleStructuredData(articleContent)} />
        <Article articleContent={articleContent} relatedArticles={relatedArticles || []} />
      </>
    );
  }

  if (seoPageData) {
    const faqItems = extractFaqItemsFromRichText(seoPageData.faq?.content);
    const faqSchema = getFaqSchema(faqItems);

    return (
      <>
        <StructuredData data={ORGANIZATION_SCHEMA} />
        <StructuredData data={SOFTWARE_APP_SCHEMA} />
        {faqSchema && <StructuredData data={faqSchema} />}
        {seoPageData.landingPageSeo && <LandingPageSEOHead entry={seoPageData} />}
        <SEOPageRenderer data={seoPageData} />
      </>
    );
  }

  if (landingPageData) {
    return (
      <>
        {landingPageData.seo && <Head {...landingPageData.seo} />}
        <LandingPageRenderer
          key={landingPageData.sys.id}
          sectionList={landingPageData.sectionList}
          content={landingPageData.content}
          breadcrumbsTheme={landingPageData.breadcrumbsTheme}
          title={landingPageData.title}
          onlyLogo={landingPageData.onlyLogo}
        />
      </>
    );
  }

  return null;
};

interface IArticlePageServerSideProps {
  // Article permutation = Tag
  articlePermutationContent: ITag | null;
  articleContent: IArticleProps | null;
  relatedArticles: ThumbnailProps[] | null;
  landingPageData: ILandingPage | null;
  // Populated for SEO Pages — /<locale>/track-<something> pages (e.g. /en/track-dhl)
  // that are SEO-driven AMP replacements. UI wiring is handled in the subsequent rendering story.
  seoPageData: ILandingPage | null;
  webVersion: string;
}

interface IArticleParams extends NodeJS.Dict<string> {
  articleTypeSlug: string;
  slug: string;
}

type Thunk = IThunk<IArticlePageServerSideProps, IArticleParams>;

// Handles SEO Pages — AMP-replacement landing pages served under the /<locale>/track-<something>
// URL pattern (e.g. /en/track-dhl, /de/track-germany). These pages are SEO-driven.
// Redis cache is handled by getServerPropsCached.
const getSeoPageData: Thunk = async ({ nextJsContext, customContext }) => {
  const { params, query } = nextJsContext;
  const { articleTypeSlug = '', slug = '' } = params || {};

  if (!SUPPORTED_LOCALES.includes(articleTypeSlug) || !slug.startsWith('track-')) return;

  const { serverSideProps, cacheServerProps, setUrlById, pageURL } = customContext;
  const isPreviewMode = checkIsPreviewModeRequest(query);

  const entry = await getLandingPage({
    slug: `${articleTypeSlug}/${slug}`,
    isPreviewMode: checkIsPreviewModeRequest(query),
    allArticlesList: [],
  });

  if (!entry) {
    return { notFound: true };
  }

  const propsToCache = { ...serverSideProps, seoPageData: entry };

  if (!isPreviewMode) {
    setUrlById(entry.sys.id, pageURL);
    cacheServerProps(pageURL, propsToCache);
  }

  return {
    props: {
      ...propsToCache,
      webVersion: CONNECTING_TO_CONTENTFUL,
    },
  };
};

getSeoPageData.debugName = 'GET_SEO_PAGE_DATA';

const getRedisUtils: Thunk = async ({ nextJsContext, customContext }) => {
  if (!customContext.serverRuntimeConfig.useDev) {
    const redisUtils = await import('@/utils/redis.utils');

    customContext.getServerPropsCached = redisUtils.getServerPropsCached;
    customContext.cacheServerProps = redisUtils.cacheServerProps;
    customContext.setUrlById = redisUtils.setUrlById;
  }

  nextJsContext.res.setHeader('Cache-Control', 'public, s-maxage=43200, stale-while-revalidate=60');
};

getRedisUtils.debugName = ThunkDebugName.GetRedisUtils;

const getServerPropsCached: Thunk = async ({ nextJsContext, customContext }) => {
  const { params, resolvedUrl } = nextJsContext;
  const { serverSideProps, getServerPropsCached: getCachedProps } = customContext;
  const { articleTypeSlug = '', slug = '' } = params || {};
  const articleTypeName = getArticleTypeNameFromSlug(articleTypeSlug);
  customContext.articleTypeName = articleTypeName;

  const isSeoPageUrl = SUPPORTED_LOCALES.includes(articleTypeSlug) && slug.startsWith('track-');

  if (!articleTypeName && !isSeoPageUrl) {
    return {
      props: {
        ...serverSideProps,
      },
      notFound: true,
    };
  }

  const pageURL = resolvedUrl.split('?')[0];
  customContext.pageURL = pageURL;

  const serverPropsCached: IArticlePageServerSideProps = JSON.parse((await getCachedProps(pageURL)) || 'null');

  if (serverPropsCached) {
    const { articleContent } = serverPropsCached;

    if (!serverSharedData.isLoggedIn && articleContent) {
      serverPropsCached.articleContent = removeOneArticleSensitiveData(articleContent);
    }

    return {
      props: {
        ...serverPropsCached,
        webVersion: CONNECTING_TO_REDIS,
      },
    };
  }
};

getServerPropsCached.debugName = ThunkDebugName.GetServerPropsCached;

const getAllThumbnailsData: Thunk = async ({ nextJsContext, customContext }) => {
  const { query, params } = nextJsContext;
  const { pageURL } = customContext;

  const slug = params?.slug || '';
  customContext.slug = slug;

  const isPreviewMode = checkIsPreviewModeRequest(query);
  customContext.isPreviewMode = isPreviewMode;

  const allArticles = await getAllThumbnails({ isPreviewMode, slug: pageURL });
  customContext.allArticles = allArticles;
};

getAllThumbnailsData.debugName = 'GET_ALL_THUMBNAILS_DATA';

const getLandingPageData: Thunk = async ({ customContext }) => {
  const { serverSideProps, setUrlById, cacheServerProps, pageURL, isPreviewMode, allArticles, slug, articleTypeName } =
    customContext;

  const isAmpPage = Object.values(AmpPageType).includes(articleTypeName);

  if (isAmpPage) {
    serverSideProps.landingPageData = await getLandingPage({
      isPreviewMode,
      slug,
      ampPageType: articleTypeName,
      allArticlesList: allArticles,
      accessToken: serverSharedData.authenticatorAccessToken,
    });

    if (serverSideProps.landingPageData) {
      if (!isPreviewMode) {
        setUrlById(serverSideProps.landingPageData.sys.id, pageURL);
        cacheServerProps(pageURL, serverSideProps);
      }

      return {
        props: {
          ...serverSideProps,
        },
      };
    }

    return {
      props: {
        ...serverSideProps,
      },
      notFound: true,
    };
  }
};

getLandingPageData.debugName = 'GET_LANDING_PAGE_DATA';

const getArticleContent: Thunk = async ({ customContext }) => {
  const { slug } = customContext;
  const { serverSideProps, setUrlById, cacheServerProps, allArticles, isPreviewMode, articleTypeName, pageURL } =
    customContext;

  serverSideProps.articleContent =
    (await getArticle({
      isPreviewMode,
      articleTypeName,
      slug,
    })) || null;

  if (serverSideProps.articleContent) {
    serverSideProps.relatedArticles =
      utils_getRelatedArticles({
        articles: allArticles,
        currentArticleTags: serverSideProps.articleContent.tags,
        currentArticleSysId: serverSideProps.articleContent.sys.id,
      }) || null;

    if (!isPreviewMode) {
      setUrlById(serverSideProps.articleContent.sys.id, pageURL);
      cacheServerProps(pageURL, serverSideProps);
    }

    if (!serverSharedData.isLoggedIn) {
      serverSideProps.articleContent = removeOneArticleSensitiveData(serverSideProps.articleContent);
    }

    return {
      props: {
        ...serverSideProps,
      },
    };
  }
};

getArticleContent.debugName = 'GET_ARTICLE_CONTENT';

const getArticlePermutationContent: Thunk = async ({ customContext }) => {
  const { serverSideProps, isPreviewMode, articleTypeName, allArticles, slug, setUrlById, cacheServerProps, pageURL } =
    customContext;
  const articlePermutationContent = await getTag({
    isPreviewMode,
    articleTypeName,
    slug,
    allArticlesList: allArticles,
  });

  if (articlePermutationContent?.hide) {
    return {
      notFound: true,
    };
  }

  serverSideProps.articlePermutationContent = articlePermutationContent;

  if (serverSideProps.articlePermutationContent) {
    const foundThumbnailSection: IThumbnailSection = serverSideProps.articlePermutationContent.sectionList?.find(
      (section: any) => capitalize(section.__typename) === ContentTypeName.ThumbnailSection,
    );

    if (foundThumbnailSection) {
      const totalThumbnails = foundThumbnailSection.latestData.length;
      const totalPages = Math.ceil(totalThumbnails / 10);

      foundThumbnailSection.pageInfo = {
        itemsPerPage: 10,
        totalPages,
      };

      foundThumbnailSection.latestData = foundThumbnailSection.latestData.slice(0, 10);
    }

    if (!isPreviewMode) {
      setUrlById(serverSideProps.articlePermutationContent.sys.id, pageURL);
      cacheServerProps(pageURL, serverSideProps);
    }

    return {
      props: {
        ...serverSideProps,
      },
    };
  }

  return {
    props: {
      ...serverSideProps,
    },
    notFound: true,
  };
};

getArticlePermutationContent.debugName = 'GET_ARTICLE_PERMUTATION_CONTENT';

export const getServerSideProps: GetServerSideProps<IArticlePageServerSideProps, IArticleParams> = async (context) => {
  return createServerSidePropsResult<IArticlePageServerSideProps, IArticleParams>({
    context,
    customContext: {
      serverSideProps: {
        articlePermutationContent: null,
        articleContent: null,
        relatedArticles: null,
        landingPageData: null,
        seoPageData: null,
        webVersion: CONNECTING_TO_CONTENTFUL,
      },
      serverRuntimeConfig,
      getServerPropsCached: () => null,
      cacheServerProps: () => null,
      setUrlById: () => null,
    },
    thunks: [
      getRedisUtils,
      getServerPropsCached,
      getSeoPageData,
      getAllThumbnailsData,
      getLandingPageData,
      getArticleContent,
      getArticlePermutationContent,
    ],
  });
};

export default ArticlePage;
